import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { IOption } from '@cmps/ng-select';
import { TranslateService } from '@ngx-translate/core';
import { timeParse } from 'd3-time-format';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import { AppConfig, CMPS_APP_CONFIG } from '../../../app.config';
import { padNum } from '../../../common/common.util';
import { mapList, mapRetrieve } from '../../../common/rest.operators';
import { User } from '../../../user/user.model';
import { UserService } from '../../../user/user.service';
import { TranslationKeyMap } from '../../coaching-insight.i18n';
import {
    ColumnConfigItem,
    getColumnConfigItems,
    Icon,
    prependEmptyOption,
    saveColumnConfigItems,
} from '../models/coaching.global.model';
import {
    ACTIVE_TAB_VIEW,
    CoachingInsightSessionKeys,
    GraphType,
    mapOppsWithPerformer,
    Metric,
    OAFilter,
    OaPerson,
    OaPersonTransformer,
    OaPopulation,
    Opportunity,
    SHOW_INACTIVE_PERFORMERS,
} from '../models/oa.model';
import { OaPeopleSort, SortDirection } from '../models/sorting.model';
import {
    getActivePerformer,
    getFilterOptions,
    saveFilterOptions,
    setActivePerformer,
} from '../opportunityalignment/coachingInsight-session-storage-functions';
import { getTeamColumnConfigItemsConfig } from '../opportunityalignment/team-oa-column-config';

@Injectable()
export class TeamOAService {
    _teamoa: BehaviorSubject<OaPerson[]> = new BehaviorSubject<OaPerson[]>(
        null,
    );

    private _allmetricslist: BehaviorSubject<IOption[]> = new BehaviorSubject<
        IOption[]
    >(null);

    private readonly _metricLookup: BehaviorSubject<Metric[]> =
        new BehaviorSubject<Metric[]>(null);

    private _metricslist: BehaviorSubject<Metric[]> = new BehaviorSubject<
        Metric[]
    >(null);

    private _componentData: BehaviorSubject<any> = new BehaviorSubject<any>(
        null,
    );

    public _teamSort: BehaviorSubject<SortDirection> =
        new BehaviorSubject<SortDirection>(null);

    public _activeView: BehaviorSubject<ACTIVE_TAB_VIEW> =
        new BehaviorSubject<ACTIVE_TAB_VIEW>(null);

    public _activeMember: BehaviorSubject<OaPerson> =
        new BehaviorSubject<OaPerson>(null);

    private readonly _user: Observable<User>;
    public _activeMetric: BehaviorSubject<Opportunity> =
        new BehaviorSubject<Opportunity>(
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            { Code: null, Description: null, Type: null } as Opportunity,
        );

    public _filteredPerformers: BehaviorSubject<boolean> =
        new BehaviorSubject<boolean>(null);

    public _forceOpenTeamOA: BehaviorSubject<boolean> =
        new BehaviorSubject<boolean>(null);

    public _rowExpanded: BehaviorSubject<boolean> =
        new BehaviorSubject<boolean>(null);

    // key-value for an observable and primary key of metrics
    // metric icon state observables
    private _ColumnConfigItemConfig: BehaviorSubject<ColumnConfigItem[]> =
        new BehaviorSubject<ColumnConfigItem[]>(null);

    private _activeMetricColumns: BehaviorSubject<number> =
        new BehaviorSubject<number>(0);

    private readonly _memberdetailsactivetab: BehaviorSubject<string> =
        new BehaviorSubject<string>(null);

    public gent: BehaviorSubject<string> = new BehaviorSubject<string>('');

    public initialTabView: ACTIVE_TAB_VIEW = null;

    public retryCount = 0;

    public totalMissedOppsCount: number = 0;
    private readonly _showActivePerformers: BehaviorSubject<boolean> =
        new BehaviorSubject<boolean>(null);

    constructor(
        private readonly _http: HttpClient,
        @Inject(CMPS_APP_CONFIG) private readonly _appConfig: AppConfig,
        private readonly _userService: UserService,
        private readonly _translateService: TranslateService,
    ) {
        this._user = this._userService.retrieve();
    }

    // public selectedFavouriteView: PersonOfInterest;
    // public userID: number ;
    // public periodTypeId: string;
    // public coachingDateToday: Date;

    public oldTeam: string;
    public currentTeam: string;
    public hasPendingRefresh: BehaviorSubject<boolean> =
        new BehaviorSubject<boolean>(false);

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    resetWidget() {
        this.oldTeam = '';
        this.currentTeam = '';
        this.initialTabView = null;

        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        this._teamoa && this._teamoa.complete();
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        this._metricslist && this._metricslist.complete();
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        this._allmetricslist && this._allmetricslist.complete();
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        this._componentData && this._componentData.complete();
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        this._teamSort && this._teamSort.complete();
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        this._activeView && this._activeView.complete();
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        this._activeMember && this._activeMember.complete();
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain, @typescript-eslint/consistent-type-assertions
        this._activeMetric &&
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            this._activeMetric.next({
                Code: null,
                Description: null,
                Type: null,
            } as Opportunity);
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        this._forceOpenTeamOA && this._forceOpenTeamOA.complete();
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        this._rowExpanded && this._rowExpanded.complete();

        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        this._ColumnConfigItemConfig && this._ColumnConfigItemConfig.complete();

        this._showActivePerformers.next(SHOW_INACTIVE_PERFORMERS);
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    initializeSubjects() {
        this._teamoa = new BehaviorSubject<OaPerson[]>(null);
        this._metricslist = new BehaviorSubject<Metric[]>(null);
        this._allmetricslist = new BehaviorSubject<IOption[]>(null);
        this._componentData = new BehaviorSubject<any>(null);
        this._teamSort = new BehaviorSubject<SortDirection>(null);
        this._activeView = new BehaviorSubject<ACTIVE_TAB_VIEW>(null);
        this._activeMember = new BehaviorSubject<OaPerson>(null);
        this._forceOpenTeamOA = new BehaviorSubject<boolean>(null);
        this._rowExpanded = new BehaviorSubject<boolean>(null);
        this._ColumnConfigItemConfig = new BehaviorSubject<ColumnConfigItem[]>(
            null,
        );
        this._activeMetricColumns = new BehaviorSubject<number>(0);
        this._filteredPerformers = new BehaviorSubject<boolean>(null);
    }

    get activeView(): Observable<ACTIVE_TAB_VIEW> {
        return this._activeView.asObservable();
    }

    get filteredPerformers(): Observable<boolean> {
        return this._filteredPerformers.asObservable();
    }

    get teamoa(): Observable<OaPerson[]> {
        return this._teamoa.asObservable();
    }

    get columns(): Observable<ColumnConfigItem[]> {
        return this._ColumnConfigItemConfig.asObservable();
    }

    get metrics(): Observable<Metric[]> {
        return this._metricslist.asObservable();
    }

    get allmetrics(): Observable<IOption[]> {
        return this._allmetricslist.asObservable();
    }

    get metricLookup(): Observable<Metric[]> {
        return this._metricLookup.asObservable();
    }

    get expected(): number {
        return 10;
    }

    get activemetriccolumns(): Observable<number> {
        return this._activeMetricColumns.asObservable();
    }

    get currentComponentData(): Observable<any> {
        return this._componentData.asObservable();
    }

    get rowExpanded(): Observable<boolean> {
        return this._rowExpanded.asObservable();
    }

    get activeMember(): Observable<OaPerson> {
        return this._activeMember.asObservable();
    }

    get activeMetric(): Observable<Opportunity> {
        return this._activeMetric.asObservable();
    }

    get activeMemberValue(): OaPerson {
        return this._activeMember.getValue();
    }

    get activeMetricValue(): Opportunity {
        return this._activeMetric.getValue();
    }

    get forceOpenTeamOA(): Observable<boolean> {
        return this._forceOpenTeamOA.asObservable();
    }

    get isTeamOAOpen(): boolean {
        return this._forceOpenTeamOA.getValue();
    }

    get isTeamOARowExpanded(): boolean {
        return this._rowExpanded.getValue();
    }

    get showActivePerformers(): Observable<boolean> {
        const _showActivePerformer = getActivePerformer();
        if (_showActivePerformer != null) {
            this._showActivePerformers.next(_showActivePerformer);
        }
        return this._showActivePerformers.asObservable();
    }

    getPerformerOA(userId: number): Observable<any> {
        const periodTypeId = 4;
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const currentDate = new Date();
        return this._http
            .get(
                `${this._appConfig.API_ENDPOINTS.COACHING}/coaching/performeroa/${userId}/${periodTypeId}`,
            )
            .pipe(
                mapRetrieve(),
                tap((data) => {
                    if (data) {
                        data.participant = {};
                    }
                }),
                map((a) => this._mapTeamResponse2(a)),
                catchError((e) => {
                    console.error('error api', e);
                    // tslint:disable-next-line: deprecation
                    return of({});
                }),
            );
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    toggleShowActivePerformers(force: boolean = null) {
        const s =
            force !== null ? force : !this._showActivePerformers.getValue();

        const t = this.getTeam();
        t.forEach((g: OaPerson) => {
            g.displayForTeam = true;
            // if the performer leaves the group before the end of the period, but still in OA
            if (!g.isCurrentlyReporting) {
                g.displayForTeam = s;
            }
        });

        this.setView(this.initialTabView, null);

        this._teamoa.next(t);
        setActivePerformer(s);
        this._showActivePerformers.next(s);

        return s;
    }

    // show an opportunity column's compressed view (1 button with numbers) or full view (multi-button opps)
    compressColumn(index: number, runSideEffect?: string): ColumnConfigItem {
        let s = this._ColumnConfigItemConfig.getValue();
        let col: ColumnConfigItem;
        s = s.map((d: ColumnConfigItem, idx: number) => {
            if (idx === index) {
                d.compress(runSideEffect);
                col = d;
            }
            return d;
        });
        saveColumnConfigItems(s);
        this._ColumnConfigItemConfig.next(s);
        return col;
    }

    // hide/show an opportunity column's compressed view (1 button with numbers)
    toggleColumn(subComponentId: any, byIndex = false): ColumnConfigItem {
        let s = this._ColumnConfigItemConfig.getValue();
        let col: ColumnConfigItem;
        s = s.map((d: ColumnConfigItem) => {
            if (
                byIndex
                    ? subComponentId === d.index
                    : d.bindToId === subComponentId
            ) {
                d.toggleItem();
                col = d;
            }
            return d;
        });
        saveColumnConfigItems(s);
        this._ColumnConfigItemConfig.next(s);
        return col;
    }

    toggleMissedOppsColumn(): void {
        let s = this._ColumnConfigItemConfig.getValue();
        let hasMissed = false;
        const team = this._teamoa.getValue();
        team.forEach((g) => {
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            if (g.missed && g.missed.length) {
                hasMissed = true;
            }
        });
        s = s.map((d: ColumnConfigItem) => {
            if (d.templateName === 'missedopps') {
                if (d.active === false) {
                    if (hasMissed === true || this.totalMissedOppsCount > 0) {
                        d.active = true;
                    } else if (!this.totalMissedOppsCount) {
                        d.active = false;
                    }
                } else {
                    if (hasMissed === false || !this.totalMissedOppsCount) {
                        d.active = false;
                    }
                }
            }
            return d;
        });
        this._ColumnConfigItemConfig.next(s);
    }

    // config item with null templateName is the member name column
    showDefaultColumns(): ColumnConfigItem[] {
        // let s = this._ColumnConfigItemConfig.getValue();
        // s = s.map((d: ColumnConfigItem) => {
        //     if (DEFAULT_COLUMNS.indexOf(d.templateName) > -1 || d.templateName === null) {
        //         if (DEFAULT_COMPRESSED_COLUMNS.indexOf(d.templateName) > -1) {
        //             d.compactView = true;
        //         }
        //         d.active = true;
        //         d.compactView = false;
        //     } else {
        //         d.active = false;
        //         d.compactView = false;
        //     }
        //     return d;
        // });
        this._ColumnConfigItemConfig.next(this.getFreshColumnConfig());
        return this._ColumnConfigItemConfig.getValue();
        // this._ColumnConfigItemConfig.next(s);
    }

    getFreshColumnConfig(): ColumnConfigItem[] {
        const columnConfigItems = getTeamColumnConfigItemsConfig(
            this._translateService,
            this.totalMissedOppsCount,
        );
        const obj = JSON.parse(
            sessionStorage.getItem(
                CoachingInsightSessionKeys.COLUMN_CONFIG_ITEMS,
            ),
        );
        // eslint-disable-next-line no-extra-boolean-cast
        if (!!obj) {
            getColumnConfigItems(columnConfigItems);
        }
        return columnConfigItems;
    }

    defaultActivePerformersView(): void {
        this.toggleShowActivePerformers(SHOW_INACTIVE_PERFORMERS);
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    triggerMemberData(e: OaPerson) {
        e.extradataloaded = true;
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    setRowExpanded(val: boolean) {
        this._rowExpanded.next(val);
    }

    retrieveRawTeamOA(
        userId: any,
        periodTypeId: any,
        currentDate: any,
    ): Observable<any> {
        let params = new HttpParams()
            .set('performerUserId', userId)
            .set('periodTypeId', periodTypeId);
        if (currentDate) {
            params = params.set('currentDate', (currentDate as Date).toJSON());
        }
        return this._http
            .get(
                `${this._appConfig.API_ENDPOINTS.COACHING}/coaching-insight/oa`,
                { params },
            )
            .pipe(mapRetrieve());
    }

    retrieveTeamOA(userId: any, periodTypeId: any, currentDate: any): void {
        let params = new HttpParams()
            .set('performerUserId', userId)
            .set('periodTypeId', periodTypeId);
        if (currentDate) {
            params = params.set(
                'currentDate',
                typeof currentDate === 'string'
                    ? currentDate
                    : currentDate.toJSON(),
            );
        }

        this._doRetrieveTeamOA(params);
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    _doRetrieveTeamOA(params: HttpParams) {
        const req1 = this._http
            .get(
                `${this._appConfig.API_ENDPOINTS.COACHING}/coaching-insight/oa`,
                { params },
            )
            .pipe(mapRetrieve());

        const sub = req1.subscribe(
            (data: any[]) => {
                this._mapTeamResponse(data);
                if (sub) sub.unsubscribe();
            },
            (error: Error) => {
                this._mapTeamResponse(error);
                const msg = error.message;
                if (sub) sub.unsubscribe();
                // redo the request if Timeout
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                if (msg && msg.indexOf('expired') && this.retryCount < 5) {
                    this.retryCount++;
                    this._doRetrieveTeamOA(params);
                }
            },
        );
    }

    retrievePerformanceData(type: GraphType, opts: any): Observable<any> {
        let params = new HttpParams();

        if (!opts.date) {
            console.error('Performance Data needs an Event Date');
            return null;
        }

        // required
        // params = params.set('date', (opts.date as Date).toJSON());
        // TODO use DataFilterOptionsModelTransformer
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions, @typescript-eslint/restrict-plus-operands
        params = params.set(
            'date',
            // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
            `${opts.date.getFullYear()}-${padNum(
                // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
                opts.date.getMonth() + 1,
                2,
            )}-${padNum(opts.date.getDate(), 2)}`,
        );

        if (opts.eventDate) {
            params = params.set('eventDate', (opts.eventDate as Date).toJSON());
        }

        if (type !== 'grid' && opts.startDate) {
            // params = params.set('startDate', (opts.startDate as Date).toJSON());
            // TODO use DataFilterOptionsModelTransformer
            // eslint-disable-next-line @typescript-eslint/restrict-template-expressions, @typescript-eslint/restrict-plus-operands
            params = params.set(
                'startDate',
                // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                `${opts.startDate.getFullYear()}-${padNum(
                    // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
                    opts.startDate.getMonth() + 1,
                    2,
                )}-${padNum(opts.startDate.getDate(), 2)}`,
            );
        }
        if (opts.superGroupId) {
            params = params.set(
                'superGroupIds',
                [opts.superGroupId].toString(),
            );
        }
        if (opts.metricId) {
            params = params.set('metricIds', opts.metricId.toString());
        }
        if (opts.userId) {
            params = params.set('userIds', opts.userId.toString());
        }
        if (opts.roleId) {
            params = params.set('roleId', opts.roleId.toString());
        }

        return (
            this._http
                // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                .get(
                    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                    `${this._appConfig.API_ENDPOINTS.PERFORMANCE_DATA}/performancedata/data/${opts.viewType}/${opts.periodType}`,
                    { params },
                )
                .pipe(mapList())
        );
    }

    retrieveCoachingPerformanceData(
        type: GraphType,
        opts: any,
    ): Observable<any> {
        let params = new HttpParams();

        if (!opts.date || !opts.startDate) {
            console.error('Performance Data needs a Start and End Date');
            return null;
        }

        // required
        params = params.set('endDate', (opts.date as Date).toJSON());

        if (type !== 'grid' && opts.startDate) {
            params = params.set('startDate', (opts.startDate as Date).toJSON());
        }
        if (opts.metricId) {
            params = params.set('metricIds', opts.metricId.toString());
        }
        if (opts.userId) {
            params = params.set('performerUserId', opts.userId.toString());
        }
        if (opts.viewType) {
            params = params.set('viewTypeId', opts.viewType.toString());
        }

        return this._http
            .get(
                `${this._appConfig.API_ENDPOINTS.COACHING}/coaching-insight/performance30day`,
                { params },
            )
            .pipe(mapList());
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    retrieveComponentChartData(member: OaPerson, component: any, metric: any) {
        this._componentData.next(null);
        return this._http
            .get(
                `app/coaching-insight/test/metriccomponentchartdata-${member.userId}.json`,
            )
            .pipe(
                mapRetrieve(),
                map((data) =>
                    this._mapComponentChartData(
                        data,
                        component,
                        member,
                        metric,
                    ),
                ),
            );
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    sortTeamByScore(ascending?: SortDirection, icon?: Icon) {
        const x = this._teamoa.getValue();

        if (!x || !x.length) return;

        this.sort(x, ascending);

        if (icon) {
            icon.toggle();
        }

        return this._teamSort.getValue();
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    sort(x: any[], direction?: SortDirection) {
        const sortdir =
            direction != null
                ? direction
                : this._teamSort.getValue() === SortDirection.ASC
                ? SortDirection.DESC
                : SortDirection.ASC;

        // set team sort direction as the given parameter,
        // or if no parameters, set to the inverse of the current team sort
        this._teamSort.next(sortdir);

        x.sort(OaPeopleSort.sortByRequirements(sortdir));
        this._teamoa.next(x);
        return sortdir;
    }

    toggleTeamOARow(item: OaPerson | number): void {
        const f = this._teamoa.getValue();
        const id = (item as OaPerson).userId
            ? (item as OaPerson).userId
            : (item as Number);

        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        let iactive: OaPerson = null;

        f.forEach((i) => {
            if (id === i.userId) {
                i.toggle();
                iactive = i;
                if (i.active) {
                    this._activeMember.next(i);
                }
            } else {
                i.toggle(false);
            }

            // deselect any active member tab
            if (i.active === false && this._memberdetailsactivetab.getValue()) {
                this.focusMemberDetailsTab(null);
            }
            this.updateActiveColumns();
        });
        // this._rowExpanded.next(iactive.active);
    }

    setActiveMember(
        item: OaPerson | number,
        clickedPerformerName = false,
    ): void {
        const f = this._teamoa.getValue();
        const id = (item as OaPerson).userId
            ? (item as OaPerson).userId
            : (item as Number);

        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const iactive: OaPerson = null;

        f.forEach((i) => {
            i.active = false;
            if (id === i.userId) {
                this._activeMember.next(i);
            }
        });
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    updateActiveColumns() {
        this._activeMetricColumns.next(this.getActiveColumns());
    }

    forceOADetails(item: OaPerson, state: boolean): void {
        item.toggle(state);
    }

    setForceOpenTeam(state: boolean): void {
        this._forceOpenTeamOA.next(state);
    }

    closeAllTeamOARows(): void {
        const f = this._teamoa.getValue();
        f.forEach((i) => {
            i.toggle(false);
        });
    }

    focusMemberDetailsTab(tabid: string): void {
        this._memberdetailsactivetab.next(tabid);
    }

    resetComponentData(): void {
        this._componentData.next(null);
    }

    initMember(UserID: any): void {
        this._teamoa.getValue().forEach((member) => {
            if (member.userId === UserID && !member.hasBeenInit) {
                const membermetrics: Metric[] = [];

                member.opportunities.forEach((opp: Opportunity) => {
                    if (!opp.metric) return;

                    const m = this._metricLookup.getValue();
                    const metric = m.find(
                        (g) => g.metricId === opp.metric.metricId,
                    );

                    const newmetric: Metric = {
                        value: metric.metricId,
                        label: metric.metricShortName,
                        metricId: metric.metricId,
                        metricInitials: metric.metricInitials,
                        metricShortName: metric.metricShortName,
                        metricName: metric.metricName,
                        oaThresholdValue: metric.oaThresholdValue,
                        rankOrder: metric.rankOrder,
                    };

                    membermetrics.push(newmetric);
                });

                this._metricslist.next(membermetrics);

                member.hasBeenInit = true;
            }
        });
    }

    setView(view: ACTIVE_TAB_VIEW, id: any): void {
        this._activeView.next(view);
    }

    unsetActives(): void {
        const g = this._teamoa.getValue();
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        if (g && g.length) {
            g.forEach((member) => {
                member.toggle(false);
            });
        }
    }

    getActiveView(): ACTIVE_TAB_VIEW {
        return this._activeView.getValue();
    }

    getTeam(): OaPerson[] {
        return this._teamoa.getValue();
    }

    getTeamo(): OaPerson[] {
        return this._teamoa.value;
    }

    decideInitialView(oaPeople: OaPopulation): ACTIVE_TAB_VIEW {
        const team = oaPeople.team;
        const self = oaPeople.self;

        this._showActivePerformers.next(SHOW_INACTIVE_PERFORMERS);

        // team is assumed to have the current user's info as well.
        // change this to point to the subordinates of the user if different.

        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        if (self && self.isInHierarchy) {
            this.initialTabView = team.length
                ? ACTIVE_TAB_VIEW.TEAM
                : ACTIVE_TAB_VIEW.SELF;
            return this.initialTabView;
        }
        this.initialTabView = ACTIVE_TAB_VIEW.OTHERS;
        return this.initialTabView;
    }

    showSelf(currentViewUserId: number): void {
        let x = this._teamoa.getValue();
        x = x.map((s) => {
            if (s.userId === currentViewUserId) s.display = true;
            else s.display = false;
            return s;
        });
        this._teamoa.next(x);
    }

    // eslint-disable-next-line @typescript-eslint/promise-function-async
    showTeam(currentViewUserId: number): Promise<boolean> {
        // for now show all

        return new Promise<boolean>((resolve) => {
            let x = this._teamoa.getValue();
            x = this.showAll(x);
            this._teamoa.next(
                x.map((s) => {
                    if (
                        currentViewUserId === s.userId ||
                        ACTIVE_TAB_VIEW[s.oaPopulation] !== ACTIVE_TAB_VIEW.TEAM
                    ) {
                        s.display = false;
                    } else {
                        s.display = true;
                    }
                    return s;
                }),
            );
            resolve(true);
        });
    }

    // eslint-disable-next-line @typescript-eslint/promise-function-async
    showOthers(currentViewUserId: number): Promise<boolean> {
        return new Promise<boolean>((resolve) => {
            let x = this._teamoa.getValue();
            x = this.showAll(x);
            this._teamoa.next(
                x.map((s) => {
                    if (
                        currentViewUserId === s.userId ||
                        ACTIVE_TAB_VIEW[s.oaPopulation] !==
                            ACTIVE_TAB_VIEW.OTHERS
                    ) {
                        s.display = false;
                    } else {
                        s.display = true;
                    }
                    return s;
                }),
            );

            resolve(true);
        });
    }

    showAll(team: OaPerson[]): OaPerson[] {
        return team.map((s: OaPerson) => {
            s.display = true;
            return s;
        });
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    toggleTeam(state: boolean = null) {
        const x = this._teamoa.getValue();
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        if (x && x.length) {
            x.forEach((j) => {
                j.toggle(state);
            });
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    setActiveMetric(opp: Opportunity) {
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        if (opp && opp.metricId) {
            this._activeMetric.next(opp);
        } else {
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            this._activeMetric.next({
                metricId: null,
                metricInitials: null,
            } as Opportunity);
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    getMetricFromId(val: any) {
        const x = this._allmetricslist.getValue();
        return x.find((d) => d.value === val);
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    toggleMetricFromGlobalFilter(id: any, state: boolean) {
        let list = this._allmetricslist.getValue();
        list = list.map((l) => {
            if (l.value === id) {
                l.disabled = state;
            }
            return l;
        });
        this._allmetricslist.next(list);
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    toggleAllMetricFromGlobalFilter(state: boolean) {
        let list = this._allmetricslist.getValue();
        if (list) {
            list = list.map((f) => {
                f.disabled = state;
                return f;
            });
        }
        this._allmetricslist.next(list);
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    resetColumnIcons() {
        const cols = this._ColumnConfigItemConfig.getValue();
        if (cols) {
            const col = cols[1];
            col.icon[0].state = false;
            col.icon[0].counter = 0;
            this._ColumnConfigItemConfig.next(cols);
        }
        this._teamSort.next(SortDirection.ASC);
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    applyGlobalFilters(filterObject: OAFilter) {
        saveFilterOptions(filterObject);
        filterObject = getFilterOptions() ? getFilterOptions() : filterObject;
        let performers = this._teamoa.getValue();

        if (!performers) return;

        performers = performers.map((performer: OaPerson) => {
            let matchedMetrics: number[] = [];

            performer.defaultMetricSort();

            // check if metric is found in a performer
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            if (
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                filterObject &&
                filterObject.filterMetrics &&
                filterObject.filterMetrics.length
            ) {
                const metrics = performer.coached.concat(performer.uncoached);

                // IF ALL SHOULD MATCH, USE BELOW
                metrics.forEach((a: Opportunity) => {
                    if (a.metric) {
                        const inFilter = filterObject.filterMetrics.filter(
                            (fm) => fm.value === a.metric.metricId,
                        );
                        if (inFilter.length) matchedMetrics.push(a.metricId);
                    }
                });

                const reordered = [];
                filterObject.filterMetrics.forEach((g) => {
                    const f = matchedMetrics.indexOf(parseInt(g.value));
                    if (f > -1) reordered.push(matchedMetrics[f]);
                });

                matchedMetrics = reordered.reverse();

                // match means all metrics should be present for the performer
                if (
                    matchedMetrics.length === filterObject.filterMetrics.length
                ) {
                    matchedMetrics.forEach((metricId) => {
                        const cIndex = performer.coached.findIndex(
                            (opp: Opportunity) => metricId === opp.metricId,
                        );
                        if (cIndex > -1) {
                            const t = performer.coached.splice(cIndex, 1);
                            performer.coached.unshift(t[0]);
                        }
                        const uIndex = performer.uncoached.findIndex(
                            (opp: Opportunity) => metricId === opp.metricId,
                        );
                        if (uIndex > -1) {
                            const t = performer.uncoached.splice(uIndex, 1);
                            performer.uncoached.unshift(t[0]);
                        }
                    });

                    performer.display = true;
                } else {
                    performer.display = false;
                }
            } else {
                performer.display = true;
            }

            if (performer.oaPopulation.toUpperCase() === ACTIVE_TAB_VIEW.SELF) {
                performer.display = true;
            }

            return performer;
        });

        switch (filterObject.filterSort) {
            case 'NumOppsAsc':
                performers.forEach((f) => {
                    f.defaultMetricSort(SortDirection.DESC);
                });
                performers.sort(
                    OaPeopleSort.sortByTotalOpportunities(SortDirection.ASC),
                );
                break;
            case 'NumOppsDesc': // default sort
                performers.forEach((f) => {
                    f.defaultMetricSort(SortDirection.DESC);
                });
                performers.sort(
                    OaPeopleSort.sortByTotalOpportunities(SortDirection.DESC),
                );
                break;
            case 'GoalPercentAsc':
                performers.forEach((p) => {
                    p.coachedSort(SortDirection.ASC);
                });
                performers.sort(
                    OaPeopleSort.sortByPercentToGoalThenRequirements(
                        SortDirection.ASC,
                    ),
                );
                break;
            case 'GoalPercentDesc':
                performers.forEach((p) => {
                    p.coachedSort();
                });
                performers.sort(
                    OaPeopleSort.sortByPercentToGoalThenRequirements(
                        SortDirection.DESC,
                    ),
                );
                break;
            case 'TargetPercentAsc':
                performers.sort(
                    OaPeopleSort.sortByPercentToTargetThenRequirements(
                        SortDirection.ASC,
                    ),
                );
                break;
            case 'TargetPercentDesc':
                performers.sort(
                    OaPeopleSort.sortByPercentToTargetThenRequirements(
                        SortDirection.DESC,
                    ),
                );
                break;
            default:
                performers.sort(OaPeopleSort.sortByRequirements());
        }

        const displayedPerformers = performers.filter(
            (d: OaPerson) => d.display === true,
        );
        const filtered = displayedPerformers.length !== performers.length;
        this._filteredPerformers.next(filtered);
        this._teamoa.next(performers);
    }

    setOaPeople(people: OaPerson[]): any {
        if (people) {
            return {
                team: people.filter(
                    (d) => d.oaPopulation === ACTIVE_TAB_VIEW.TEAM,
                ),
                others: people.filter(
                    (d) => d.oaPopulation === ACTIVE_TAB_VIEW.OTHERS,
                ),
                self: people.find(
                    (d) => d.oaPopulation === ACTIVE_TAB_VIEW.SELF,
                ),
            };
        }
    }

    private _mapTeamResponse2(data): any {
        if (!data || !data.participant) {
            this._teamoa.next(null);
            this._metricslist.next(null);
            this._allmetricslist.next(null);
            return false;
        }

        this.totalMissedOppsCount = 0;

        this.currentTeam = JSON.stringify(data.participant);

        this._teamSort.next(SortDirection.ASC);

        // const teamoa = new OaPersonTransformer().transform(data);

        const teamoa = mapOppsWithPerformer(
            data.participant,
            data.opportunities,
        );

        if (!teamoa) return;

        this._teamoa.next(teamoa);

        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const uniquemetrics: IOption[] = [];

        const opps = data.opportunities;
        const metrics = [];
        const uniqueopps = [];

        if (teamoa.length === 1 && teamoa[0].missed) {
            this.totalMissedOppsCount = teamoa[0].missed.length;
        } else {
            teamoa.forEach((g) => {
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                if (g.missed && g.missed.length) {
                    this.totalMissedOppsCount += g.missed.length;
                }
            });
        }

        opps.forEach((f) => {
            if (f.parentMetricId == null) {
                const m = f.metricId.toString();
                const found = uniqueopps.find((d) => d.value === m);
                const mymetric = metrics.find(
                    (g) => g.metricId.toString() === m,
                );
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                const opt: IOption = {} as IOption;
                if (!found && mymetric) {
                    opt.label = mymetric.metricName;
                    opt.value = mymetric.metricId;
                    opt.disabled = false;
                    uniqueopps.push(opt);
                }
            }
        });

        uniqueopps.sort((a, b) => {
            if (a.label == null) return -1;
            if (b.label == null) return 1;
            if (a.label.toLowerCase() < b.label.toLowerCase()) return -1;
            if (a.label.toLowerCase() > b.label.toLowerCase()) return 1;
            return 0;
        });

        prependEmptyOption(
            uniqueopps,
            this._translateService.instant(
                TranslationKeyMap._7448_PLEASE_SELECT_,
            ),
        );

        this._allmetricslist.next(uniqueopps);
        this._metricLookup.next([] as Metric[]);

        return { performers: this._teamoa.getValue() };
    }

    private _mapTeamResponse(data): void {
        if (!data || !data.oaPeople) {
            this._teamoa.next(null);
            this._metricslist.next(null);
            this._allmetricslist.next(null);
            return;
        }

        this.totalMissedOppsCount = 0;

        this.currentTeam = JSON.stringify(data);

        this._teamSort.next(SortDirection.ASC);

        const teamoa = new OaPersonTransformer().transform(data);

        if (!teamoa) return;

        this._teamoa.next(teamoa);

        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const uniquemetrics: IOption[] = [];

        const opps = data.opportunities;
        const metrics = data.metrics;
        const uniqueopps = [];

        if (teamoa.length === 1 && teamoa[0].missed) {
            this.totalMissedOppsCount = teamoa[0].missed.length;
        } else {
            teamoa.forEach((g) => {
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                if (g.missed && g.missed.length) {
                    this.totalMissedOppsCount += g.missed.length;
                }
            });
        }

        opps.forEach((f) => {
            if (f.parentMetricId == null) {
                const m = f.metricId.toString();
                const found = uniqueopps.find((d) => d.value === m);
                const mymetric = metrics.find(
                    (g) => g.metricId.toString() === m,
                );
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                const opt: IOption = {} as IOption;
                if (!found && mymetric) {
                    opt.label = mymetric.metricName;
                    opt.value = mymetric.metricId;
                    opt.disabled = false;
                    uniqueopps.push(opt);
                }
            }
        });

        uniqueopps.sort((a, b) => {
            if (a.label == null) return -1;
            if (b.label == null) return 1;
            if (a.label.toLowerCase() < b.label.toLowerCase()) return -1;
            if (a.label.toLowerCase() > b.label.toLowerCase()) return 1;
            return 0;
        });

        prependEmptyOption(
            uniqueopps,
            this._translateService.instant(
                TranslationKeyMap._7448_PLEASE_SELECT_,
            ),
        );

        this._allmetricslist.next(uniqueopps);
        this._metricLookup.next(data.metrics as Metric[]);
    }

    initializeColumnConfig(): number {
        this.showDefaultColumns();
        return this.totalMissedOppsCount;
    }

    private getActiveColumns(): number {
        const cols = this._ColumnConfigItemConfig.getValue();
        let c = 0;
        if (cols) {
            cols.forEach((g) => {
                if (g.active === true) c++;
            });
        }
        return c;
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    private _mapComponentChartData(
        data: any,
        component: any,
        member: OaPerson,
        metric: any,
    ) {
        const parsedtg = timeParse('%Y-%m-%d');
        data = data.componentdata;

        if (data) {
            const componentobj = data.find(
                (s) =>
                    s.Code ===
                    (typeof component === 'string'
                        ? component
                        : component.Code),
            );

            if (!componentobj.data) {
                this._componentData.next(null);
                return;
            }

            let newdata = componentobj.data.map((d) => {
                return {
                    x: parsedtg(d.date),
                    y: Number(d.value),
                };
            });

            newdata = newdata.sort((a, b) => {
                return b.x.getTime() - a.x.getTime();
            });

            return {
                prop: componentobj.Code,
                data: newdata,
            };
        }

        return null;
    }
}
